import { driver, type Driver, type DriveStep } from "driver.js";
import type { ReactNode } from "react";
import { createContext, useCallback, useEffect, useMemo, useRef } from "react";

import "driver.js/dist/driver.css";
import "./tour.css";
import { TOURS } from "./tours";
import type { TourDefinition, TourId, TourStepDefinition } from "./types";

/**
 * Persistence layer for "has the user completed this tour?".
 *
 * NOTE: today this is just a localStorage key. When we add a user-prefs DB
 * table for onboarding state, swap the body of these two functions to call
 * the API (e.g. PATCH /users/me/preferences { tours: { ... } }) — the
 * provider/consumer surface does not need to change.
 */
const STORAGE_PREFIX = "autopilotqa.tour.";
const completedKey = (id: TourId) => `${STORAGE_PREFIX}${id}.completed`;

// Persisted in localStorage; can be migrated to user-prefs DB later
function readCompleted(id: TourId): boolean {
  try {
    return window.localStorage.getItem(completedKey(id)) === "1";
  } catch {
    // localStorage unavailable (private mode, SSR) — treat as not completed
    return false;
  }
}

function writeCompleted(id: TourId, value: boolean): void {
  try {
    if (value) {
      window.localStorage.setItem(completedKey(id), "1");
    } else {
      window.localStorage.removeItem(completedKey(id));
    }
  } catch {
    /* swallow — best-effort persistence */
  }
}

/** Are reduced-motion preferences set? Used to disable smoothScroll if reduced. */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export interface TourContextValue {
  /** Start a tour by id. No-op if a tour is already running. */
  startTour: (tourId: TourId) => void;
  /** Has the user previously completed (or skipped) this tour? */
  isTourCompleted: (tourId: TourId) => boolean;
  /** Force-clear the "completed" flag for a tour (used by the Help menu). */
  resetTour: (tourId: TourId) => void;
}

export const TourContext = createContext<TourContextValue | undefined>(
  undefined,
);

interface TourProviderProps {
  children: ReactNode;
  /**
   * Optional override map — primarily for tests. Defaults to the production
   * TOURS registry shipped from `./tours`.
   */
  tours?: Record<TourId, TourDefinition>;
}

/**
 * Provider that exposes driver.js-powered product tours via React context.
 *
 * Mount this once near the app root, *inside* your router so step `element`
 * selectors resolve against the currently rendered page.
 */
export function TourProvider({ children, tours = TOURS }: TourProviderProps) {
  // We keep one Driver instance alive at a time. Mounting/destroying the
  // driver inside startTour() avoids leaking listeners between runs.
  const activeDriverRef = useRef<Driver | null>(null);

  // Cleanup if the provider unmounts mid-tour
  useEffect(() => {
    return () => {
      activeDriverRef.current?.destroy();
      activeDriverRef.current = null;
    };
  }, []);

  const isTourCompleted = useCallback(
    (tourId: TourId) => readCompleted(tourId),
    [],
  );

  const resetTour = useCallback(
    (tourId: TourId) => writeCompleted(tourId, false),
    [],
  );

  const startTour = useCallback(
    (tourId: TourId) => {
      const definition = tours[tourId];
      if (!definition) {
        console.warn(`[TourProvider] Unknown tour id: "${tourId}"`);
        return;
      }

      // Don't start a second tour over an existing one
      if (activeDriverRef.current?.isActive()) {
        return;
      }

      const reduceMotion = prefersReducedMotion();
      const steps: DriveStep[] = definition.steps.map(buildDriveStep);

      const persistAndCleanup = () => {
        writeCompleted(tourId, true);
        activeDriverRef.current = null;
      };

      const instance = driver({
        showProgress: true,
        animate: !reduceMotion,
        smoothScroll: !reduceMotion,
        allowClose: true,
        overlayColor: "#0b0d12",
        overlayOpacity: 0.55,
        stagePadding: 6,
        stageRadius: 10,
        popoverOffset: 10,
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "Done",
        progressText: "{{current}} of {{total}}",
        popoverClass: "autopilot-tour",
        steps,
        onDestroyed: persistAndCleanup,
        onCloseClick: () => {
          // Skip-tour click — also persist so we don't re-prompt.
          persistAndCleanup();
          instance.destroy();
        },
      });

      activeDriverRef.current = instance;
      instance.drive();
    },
    [tours],
  );

  const value = useMemo<TourContextValue>(
    () => ({ startTour, isTourCompleted, resetTour }),
    [startTour, isTourCompleted, resetTour],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

type DriverPopoverSide = Exclude<
  NonNullable<NonNullable<DriveStep["popover"]>["side"]>,
  undefined
>;

function toDriverPopoverSide(
  side: TourStepDefinition["side"],
): DriverPopoverSide | undefined {
  // "over" existed in older tour configs but is not supported by the
  // current driver.js Side type.
  if (!side || side === "over") return undefined;
  return side;
}

/** Convert our simplified step shape to a driver.js DriveStep. */
function buildDriveStep(step: TourStepDefinition): DriveStep {
  return {
    element: step.element,
    popover: {
      title: step.title,
      description: step.description,
      side: toDriverPopoverSide(step.side),
      align: step.align,
    },
  };
}
